'use client';

import { forwardRef, useMemo } from 'react';

import { css, cx } from '@/styled-system/css';

import { TableRootContext } from './context/table-root.context';
import { useSortableTable } from './hooks/use-sortable-table';
import { tableRootRecipe } from './styles/table-root.styles';
import type { TableRootPropsInterface } from './types/table-root.types';

const DEFAULT_ALIGNMENTS: NonNullable<TableRootPropsInterface['alignments']> = [];

/**
 * A native [`<table>`](https://developer.mozilla.org/en-US/docs/Web/HTML/Element/table) element.
 *
 * @since 8.0.0
 */
export const TableRoot = forwardRef<HTMLTableElement, TableRootPropsInterface>(
  (props: TableRootPropsInterface, forwardedRef) => {
    const isSortControlled = 'sortBy' in props && 'sortDirection' in props;

    const {
      alignments = DEFAULT_ALIGNMENTS,
      appearance = 'grid',
      className,
      css: cssProp,
      defaultSortBy,
      defaultSortDirection,
      onSortChange,
      size = 'md',
      sortBy,
      sortDirection,
      sortDirectionOrder,
      ...rest
    } = props;

    const sortableContext = useSortableTable({
      defaultSortBy,
      defaultSortDirection,
      isControlled: isSortControlled,
      onSortChange,
      sortBy,
      sortDirection,
      sortDirectionOrder,
    });

    const styles = css(tableRootRecipe.raw({ appearance, size }), cssProp);

    const contextValue = useMemo(
      () => ({
        alignments,
        appearance,
        size,
        ...sortableContext,
      }),
      [alignments, appearance, size, sortableContext],
    );

    return (
      <TableRootContext.Provider value={contextValue}>
        <table
          ref={forwardedRef}
          data-c11n-component="Table.Root"
          data-c11n-version={__C11N_VERSION__}
          className={cx('c11n', styles, className)}
          {...rest}
        />
      </TableRootContext.Provider>
    );
  },
);

TableRoot.displayName = 'Table.Root';
